import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { Loading } from '../../../../ui/loading/Loading';
import { ContentEditorModal, ContentEditorValues } from './ContentEditorModal';
import { CommentsAndAttachments } from './CommentsAndAttachments';

type Row = {
    id: number;
    title: string | null;
    story: string | null;
    type: string;
    listOrder: number | null;
    [dateField: string]: unknown;
};

type Props = {
    organizationId: string;
    teamId: string;
    initiativeId: string;
    allowEdit: boolean;
    currentUserId?: number;
    sectionLabel: string;
    entityLabel: string;
    entityType: string;
    messageType: string;
    entityParam: 'milestone_id' | 'next_step_id';
    dateField: string;
    dateLabel: string;
    listUrl: string;
    reorderUrl: string;
    itemUrl: (id: number) => string;
    listResponseKey: string;
};

/**
 * Shared accordion for Milestones and Next Steps — structurally identical tables
 * (§0.1 of the migration plan, same note in StrategiesService), each with a top-level
 * "items" tab and a "Discussion" tab (type=Message rows on the same table). One
 * component parameterized by entity, rather than two near-duplicate files. Subitems
 * are out of scope (§4c note); Comments/Links/Uploads on each row are wired via the
 * shared CommentsAndAttachments panel (§4d).
 */
export const MilestoneStyleAccordion = ({
    organizationId,
    teamId,
    initiativeId,
    allowEdit,
    currentUserId,
    sectionLabel,
    entityLabel,
    entityType,
    messageType,
    entityParam,
    dateField,
    dateLabel,
    listUrl,
    reorderUrl,
    itemUrl,
    listResponseKey,
}: Props) => {
    // Confirmed live 2026-08-27: MILESTONES/NEXT STEPS are collapsed by default
    // (worksheet_v1.php's `ws-v1-details-section-header` bars), same as DETAILS.
    const [sectionOpen, setSectionOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'items' | 'discussion'>('items');
    const [items, setItems] = useState<Row[]>([]);
    const [messages, setMessages] = useState<Row[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [modal, setModal] = useState<{ mode: 'create' | 'edit'; editing?: Row } | null>(null);
    const [saving, setSaving] = useState(false);

    const fetchItems = () => {
        setLoading(true);
        axios
            .get(listUrl)
            .then((response) => setItems(response.data[listResponseKey] ?? []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };

    const fetchMessages = () => {
        setLoading(true);
        axios
            .get(listUrl, { params: { type: messageType } })
            .then((response) => setMessages(response.data[listResponseKey] ?? []))
            .catch(() => setMessages([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (activeTab === 'items') {
            fetchItems();
        } else {
            fetchMessages();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const rows = activeTab === 'items' ? items : messages;
    const setRows = activeTab === 'items' ? setItems : setMessages;
    const refetch = activeTab === 'items' ? fetchItems : fetchMessages;

    const toggleExpand = (id: number) => {
        setExpandedId((current) => (current === id ? null : id));
    };

    const moveRow = (index: number, direction: -1 | 1) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= rows.length) {
            return;
        }
        const reordered = [...rows];
        [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
        setRows(reordered);
        axios.put(reorderUrl, { ordered_ids: reordered.map((row) => row.id) }).catch(() => {
            Swal.fire({ icon: 'error', title: 'Reorder failed', text: 'This could not be reordered.' });
            refetch();
        });
    };

    const removeRow = async (row: Row) => {
        const result = await Swal.fire({
            title: 'Remove this?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove',
        });
        if (!result.isConfirmed) {
            return;
        }
        try {
            await axios.delete(itemUrl(row.id));
            refetch();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Delete failed', text: 'This could not be removed.' });
        }
    };

    const handleModalSubmit = async (values: ContentEditorValues) => {
        setSaving(true);
        try {
            if (modal?.mode === 'edit' && modal.editing) {
                await axios.put(itemUrl(modal.editing.id), {
                    title: values.title,
                    story: values.description || null,
                    [dateField]: values.date || null,
                });
            } else {
                await axios.post(listUrl, {
                    title: values.title,
                    story: values.description || null,
                    [dateField]: values.date || null,
                    type: activeTab === 'discussion' ? messageType : entityType,
                });
            }
            setModal(null);
            refetch();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This could not be saved.' });
        } finally {
            setSaving(false);
        }
    };

    const untitledLabel = activeTab === 'discussion' ? 'Untitled topic' : `Untitled ${entityLabel.toLowerCase()}`;
    const emptyLabel = activeTab === 'discussion' ? 'No topics yet.' : `No ${entityLabel.toLowerCase()}s yet.`;

    const sectionModifierClass = entityParam === 'milestone_id' ? 'strategies-section--milestones' : 'strategies-section--next-steps';

    return (
        <div className={`strategies-details-accordion ${sectionModifierClass} mt-4`}>
            <div
                className="strategies-section-toggle d-flex justify-content-between align-items-center"
                role="button"
                aria-expanded={sectionOpen}
                data-testid="section-toggle"
                onClick={() => setSectionOpen((current) => !current)}
            >
                <h4 className="mb-0">{sectionLabel}</h4>
                <span className="strategies-section-chevron">{sectionOpen ? '▾' : '▸'}</span>
            </div>

            {sectionOpen && (
            <>
            <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                {/* Confirmed live 2026-08-27 (worksheet_v1.php's `ws-v1-nextsteps-tab-add-btn`/
                `nextsteps-add-message-btn`, same pattern as `_next_steps.php`/`_milestones.php`):
                each tab pill carries its own inline "+" — no separate "+ Add X" button off to
                the side, matching the fix already made for Categories/Discussion. Same
                deliberate simplification as that fix: clicking a tab's "+" also switches to
                that tab, since the create modal's fields are driven by `activeTab`. */}
                <div className="btn-group" role="tablist">
                    <Button
                        as="span"
                        size="sm"
                        variant={activeTab === 'items' ? 'warning' : 'outline-secondary'}
                        onClick={() => setActiveTab('items')}
                        className="d-flex align-items-center gap-2"
                    >
                        {sectionLabel}
                        {allowEdit && (
                            <button
                                type="button"
                                aria-label={`Add ${entityLabel}`}
                                title={`Add ${entityLabel}`}
                                className="strategies-tab-add-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveTab('items');
                                    setModal({ mode: 'create' });
                                }}
                            >
                                +
                            </button>
                        )}
                    </Button>
                    <Button
                        as="span"
                        size="sm"
                        variant={activeTab === 'discussion' ? 'warning' : 'outline-secondary'}
                        onClick={() => setActiveTab('discussion')}
                        className="d-flex align-items-center gap-2"
                    >
                        {activeTab === 'discussion' ? `Discussion (${messages.length})` : 'Discussion'}
                        {allowEdit && (
                            <button
                                type="button"
                                aria-label="Add topic"
                                title="Add topic"
                                className="strategies-tab-add-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveTab('discussion');
                                    setModal({ mode: 'create' });
                                }}
                            >
                                +
                            </button>
                        )}
                    </Button>
                </div>
            </div>

            {loading ? (
                <Loading />
            ) : rows.length === 0 ? (
                <p className="text-muted">{emptyLabel}</p>
            ) : (
                <div className="d-flex flex-column gap-2">
                    {rows.map((row, index) => {
                        const expanded = expandedId === row.id;
                        const dateValue = row[dateField] as string | null | undefined;
                        return (
                            <div key={row.id} className="strategies-category-card border rounded p-3">
                                <div className="d-flex justify-content-between align-items-start strategies-card-header-row">
                                    <div className="flex-grow-1" role="button" onClick={() => toggleExpand(row.id)}>
                                        <span className="fw-bold">{row.title || untitledLabel}</span>
                                        {activeTab === 'items' && dateValue && (
                                            <span className="strategies-target-date ms-2">{dateValue}</span>
                                        )}
                                    </div>
                                    {allowEdit && (
                                        <div className="d-flex gap-1">
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                disabled={index === 0}
                                                onClick={() => moveRow(index, -1)}
                                                aria-label="Move up"
                                            >
                                                ↑
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                disabled={index === rows.length - 1}
                                                onClick={() => moveRow(index, 1)}
                                                aria-label="Move down"
                                            >
                                                ↓
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => setModal({ mode: 'edit', editing: row })}
                                            >
                                                Edit
                                            </Button>
                                            <Button size="sm" variant="outline-danger" onClick={() => removeRow(row)}>
                                                Remove
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {expanded && (
                                    <div className="mt-2">
                                        {row.story && <div className="text-muted small mb-2">{row.story}</div>}
                                        <CommentsAndAttachments
                                            organizationId={organizationId}
                                            teamId={teamId}
                                            initiativeId={initiativeId}
                                            entityParam={entityParam}
                                            entityId={row.id}
                                            allowEdit={allowEdit}
                                            currentUserId={currentUserId}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            </>
            )}

            {modal && (
                <ContentEditorModal
                    show
                    title={
                        activeTab === 'discussion'
                            ? modal.mode === 'edit'
                                ? 'Edit Topic'
                                : 'New Topic'
                            : modal.mode === 'edit'
                            ? `Edit ${entityLabel}`
                            : `New ${entityLabel}`
                    }
                    titleLabel="Title"
                    // Confirmed live 2026-08-26 (New Milestone/New Next Step) and again
                    // 2026-08-27 by reading worksheet_v1.js/`#milestoneFormModal` directly:
                    // Milestone, Next Step, AND their nested discussion topics all share one
                    // static Yii modal (`openMilestoneFormModal`/`milestoneFormApplyModalTitle`)
                    // whose Title field always reads "Title (required)" with plain "Title"
                    // placeholder, and whose Story field always placeholders "Optional
                    // details" — the same on both tabs, not just the items tab as an earlier
                    // pass had assumed. Applied unconditionally here to match.
                    titleRequiredSuffix=" (required)"
                    titlePlaceholder="Title"
                    storyPlaceholder="Optional details"
                    saving={saving}
                    showDate={activeTab === 'items'}
                    dateLabel={dateLabel}
                    initialValues={
                        modal.editing
                            ? {
                                  title: modal.editing.title ?? '',
                                  description: modal.editing.story ?? '',
                                  date: (modal.editing[dateField] as string | null) ?? '',
                              }
                            : undefined
                    }
                    onHide={() => setModal(null)}
                    onSubmit={handleModalSubmit}
                />
            )}
        </div>
    );
};

